import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  Renderer2,
  ViewChild
} from '@angular/core';
import { ListComponentGeneric } from '../../../../ui/generics';
import { NotificationService } from '../../../../core/services';
import { IArticle } from '../../../../core/interfaces';
import { ArticleLabelsService } from '../../core/services/ArticleLabelsService';
import { ArticleLabelsErrorResolverService } from '../../core/services/ArticleLabelsErrorResolverService';
import { ArticleLabelTypes, isInstantArticleLabelType } from '../../core/enums/ArticleLabelTypes';
import { OpenPdfDirective } from '../../../../core/directives';
import { ArticleEtikettenModalsService } from '../../core/services/ArticleEtikettenModalsService';
import { EtikettenOverviewService } from '../../../etiketten/core/services/EtikettenOverviewService';
import { IEtikettOverview } from '../../../etiketten/core/interfaces/IEtikettOverview';
import {
  canPreviewEtikett,
  canRegenerateEtikett,
  isPersistedArticleLabel,
  toArticleLabel,
} from '../../../etiketten/core/etikett-row';
import { EtikettPreviewModalComponent } from '../../../etiketten/previews/etikett-preview-modal.component';
import { finalize, switchMap } from 'rxjs/operators';

@Component({
    selector: 'app-article-labels-table',
    templateUrl: './article-labels-table.component.html',
    styleUrl: './article-labels-table.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [OpenPdfDirective],
    standalone: false
})
export class ArticleLabelsTableComponent extends ListComponentGeneric<IEtikettOverview> {
  @ViewChild('etikettPreviewModalComponent') etikettPreviewModalComponent: EtikettPreviewModalComponent;
  @Input() set article(val: IArticle) {
    const changed = val?.id !== this._article?.id;
    this._article = val;
    if (val?.id) {
      this._articleLabelsService.setBaseUrl(val.id);
      if (changed) {
        this.onRefreshList(true);
      }
    }
  }

  protected searchColumns = ['variantName', 'ean', 'etikettenartName', 'status'];
  protected readonly exportedTableVisibleName = 'Artikel Etiketten';
  protected readonly defaultSorting: { sortField: string; sortOrder: string; } = {
    sortField: 'variantName',
    sortOrder: 'ascend'
  };

  private _article: IArticle;

  public constructor(
    protected _service: EtikettenOverviewService,
    protected _cdr: ChangeDetectorRef,
    protected _notifyService: NotificationService,
    protected renderer: Renderer2,
    protected _errorResolver: ArticleLabelsErrorResolverService,
    protected _openPdfDirective: OpenPdfDirective,
    private readonly _articleLabelsService: ArticleLabelsService,
    private readonly _etikettenModals: ArticleEtikettenModalsService,
  ) {
    super(_service, _cdr, _notifyService, renderer, _errorResolver);
  }

  public get article(): IArticle {
    return this._article;
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this._etikettenModals.setRefresh(() => this.onRefreshList(false));
  }

  public ngOnDestroy(): void {
    this._etikettenModals.setRefresh();
    super.ngOnDestroy();
  }

  public load(
    pageIndex: number,
    pageSize: number,
    sortField: string | null,
    sortOrder: string | null,
    filter: { key: string; value: string[] }[],
    search: { key: string; value: string[] }[],
    additionalFilters: { key: string; value: string[] }[] = [],
  ): void {
    if (!this._article?.id) {
      return;
    }
    const extra = additionalFilters.filter(x => x.key !== 'ArticleId');
    extra.push({ key: 'ArticleId', value: [String(this._article.id)] });
    super.load(pageIndex, pageSize, sortField, sortOrder, filter, search, extra);
  }

  public readonly canPreview = canPreviewEtikett;
  public readonly isPersistedArticleLabel = isPersistedArticleLabel;
  public readonly canRegenerate = canRegenerateEtikett;

  public preview(row: IEtikettOverview): void {
    this.etikettPreviewModalComponent.showModal(row);
  }

  public openAddLabel(): void {
    this._etikettenModals.add?.showModal(this.article);
  }

  public anlegen(row: IEtikettOverview): void {
    if (!row.canCreate) {
      return;
    }
    if (row.source === 'OwnBrand') {
      this._etikettenModals.ownBrand?.showModal(row.articleVariantId);
      return;
    }

    const type = row.etikettenart as ArticleLabelTypes;

    if (isInstantArticleLabelType(type)) {
      this.loading = true;
      this._cdr.detectChanges();
      this._articleLabelsService.createInstant(this._article.id, row.articleVariantId, type).subscribe({
        next: () => {
          this._notifyService.pushSuccess('Erfolg', 'Etikett wurde erstellt.');
          this.onRefreshList(false);
        },
        error: this.parseError
      });
      return;
    }

    const round = this._etikettenModals.round;
    if (!round) {
      return;
    }
    this._articleLabelsService.setBaseUrl(this._article.id);
    round.labelType = type;
    round.article = this.article;
    round.articleVariantIds = [row.articleVariantId];
    round.showModal(null);
  }

  public edit(row: IEtikettOverview): void {
    if (row.source === 'OwnBrand') {
      this._etikettenModals.ownBrand?.showModal(row.articleVariantId);
      return;
    }
    if (row.canEdit) {
      this._articleLabelsService.setBaseUrl(this._article.id);
      const round = this._etikettenModals.round;
      if (!round) {
        return;
      }
      round.article = this.article;
      round.articleVariantIds = [row.articleVariantId];
      round.showModal(toArticleLabel(row));
    }
  }

  public deleteLabel(row: IEtikettOverview): void {
    if (!this.isPersistedArticleLabel(row)) {
      return;
    }
    this.loading = true;
    this._cdr.detectChanges();

    this._articleLabelsService.delete(row.id.toString()).subscribe({
      next: () => {
        this._notifyService.pushSuccess('Erfolgreich', 'Etikett gelöscht');
        this.onRefreshList(false);
      },
      error: this.parseError
    });
  }

  public regenerateLabel(row: IEtikettOverview): void {
    if (!this.isPersistedArticleLabel(row)) {
      return;
    }
    this.loading = true;
    this._cdr.detectChanges();

    this._articleLabelsService.regenerateLabel(row.id.toString()).subscribe({
      next: () => {
        this._notifyService.pushSuccess('Erfolgreich', 'Etikett regeneriert.');
        this.onRefreshList(false);
      },
      error: this.parseError
    });
  }

  public regenerateArticleLabels(): void {
    this.loading = true;
    this._cdr.detectChanges();

    this._articleLabelsService.regenerateLabels().subscribe({
      next: () => {
        this._notifyService.pushSuccess('Erfolgreich', 'Alle Etiketten regeneriert.');
        this.onRefreshList(false);
      },
      error: this.parseError
    });
  }

  public printAllLabels(): void {
    this.loading = true;
    this._cdr.detectChanges();

    const fromTable = this.data.results.map(row => row.labelKey).filter(Boolean);
    this._articleLabelsService.getArticleLabelsKeys().pipe(
      switchMap(res => {
        const keys = [...new Set([
          ...fromTable,
          ...res.map(label => label.labelKey).filter(Boolean),
        ])];
        return this._openPdfDirective.openKeys(keys);
      }),
      finalize(() => {
        this.loading = false;
        this._cdr.detectChanges();
      })
    ).subscribe({
      error: this.parseError
    });
  }
}
